<script setup lang="ts">
import InternalLinks from "@/components/seo/InternalLinks.vue";
import { seoConfig } from "@/config/seo.config";
import { zikrPresets } from "@/data/zikr";
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";

const router = useRouter();
const savedCounts = ref<Record<string, number>>({});

const postSalahKeys = ["subhanallah", "alhamdulillah", "allahuakbar"];
const postSalahPresets = computed(() =>
  zikrPresets.filter((z) => postSalahKeys.includes(z.key)),
);
const otherPresets = computed(() =>
  zikrPresets.filter((z) => !postSalahKeys.includes(z.key)),
);

const postSalahComplete = computed(() =>
  postSalahPresets.value.every((z) => (savedCounts.value[z.key] ?? 0) >= z.defaultTarget),
);
const totalPostSalahTarget = computed(() =>
  postSalahPresets.value.reduce((sum, z) => sum + z.defaultTarget, 0),
);
const totalPostSalahProgress = computed(() =>
  postSalahPresets.value.reduce(
    (sum, z) => sum + progressFor(z.key, z.defaultTarget),
    0,
  ),
);

function openZikr(key: string) {
  router.push("/zikr/count?d=" + key);
}

function goBack() {
  if (process.client && window.history.length > 1) {
    router.back();
    return;
  }

  router.push("/");
}

function progressFor(key: string, target: number) {
  return Math.min(savedCounts.value[key] ?? 0, target);
}

function progressPercent(key: string, target: number) {
  if (target <= 0) return 0;
  return Math.round((progressFor(key, target) / target) * 100);
}

function isCardComplete(key: string, target: number) {
  return progressFor(key, target) >= target;
}

onMounted(() => {
  const counts: Record<string, number> = {};

  for (const z of zikrPresets) {
    const raw = localStorage.getItem("zikr_" + z.key);
    if (!raw) {
      continue;
    }

    try {
      const parsed = JSON.parse(raw) as { count?: number };
      counts[z.key] = typeof parsed.count === "number" ? parsed.count : 0;
    } catch {
      counts[z.key] = 0;
    }
  }

  savedCounts.value = counts;
});

useHead({
  title: seoConfig.zikr.title,
  meta: [
    { name: "description", content: seoConfig.zikr.description },
    {
      name: "keywords",
      content:
        "zikr counter, dhikr counter, tasbih counter, tasbeeh counter, digital tasbih, adhkar, azkar, morning adhkar, evening adhkar, subhanallah alhamdulillah allahu akbar",
    },
    { property: "og:title", content: seoConfig.zikr.title },
    { property: "og:description", content: seoConfig.zikr.description },
    { property: "og:type", content: "website" },
    { property: "og:url", content: seoConfig.zikr.canonical },
    { property: "og:site_name", content: "Rozay" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: seoConfig.zikr.title },
    { name: "twitter:description", content: seoConfig.zikr.description },
  ],
  link: [{ rel: "canonical", href: seoConfig.zikr.canonical }],
});
</script>

<template>
  <div class="max-w-xl mx-auto px-4 py-6 space-y-6">
    <div class="flex items-center py-1">
      <button @click="goBack" class="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
        Back
      </button>
    </div>

    <!-- Title -->
    <div class="text-center space-y-1">
      <h1 class="text-2xl font-semibold tracking-tight text-gray-900 dark:text-gray-100">Zikr</h1>
      <p class="text-sm text-gray-500 dark:text-gray-400">Post-Salah Tasbih (100)</p>
      <p
        class="text-sm text-gray-500 dark:text-gray-400 transition-all duration-300 ease-out"
        :class="postSalahComplete ? 'text-emerald-700 dark:text-emerald-300 scale-105' : ''"
      >
        {{ totalPostSalahProgress }} / {{ totalPostSalahTarget }} Complete
      </p>
      <transition name="fade-in">
        <p
          v-if="postSalahComplete"
          class="text-sm text-emerald-700 dark:text-emerald-300 mt-1"
        >
          Tasbih Complete
        </p>
      </transition>
      <transition name="fade-in">
        <p
          v-if="postSalahComplete"
          class="text-xs text-emerald-700/90 dark:text-emerald-300/90"
        >
          Allah qabool kare.
        </p>
      </transition>
    </div>

    <!-- Post Salah Presets -->
    <div class="space-y-3">
      <button
        v-for="z in postSalahPresets"
        :key="z.key"
        @click="openZikr(z.key)"
        :class="[
          'w-full block rounded-xl py-4 px-4 border active:scale-[0.98] text-[15px] font-medium tracking-tight text-left transition-colors',
          isCardComplete(z.key, z.defaultTarget)
            ? 'border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-100 scale-[1.01]'
            : 'bg-gray-50 border-gray-100 hover:bg-gray-100 text-gray-900 dark:text-gray-100 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700',
        ]"
      >
        <div class="flex items-center justify-between gap-3">
          <span>{{ z.label }} ({{ z.defaultTarget }})</span>
          <span
            v-if="isCardComplete(z.key, z.defaultTarget)"
            class="text-xs font-semibold uppercase tracking-wide"
          >
            Complete
          </span>
        </div>
        <div
          class="text-xs mt-1"
          :class="isCardComplete(z.key, z.defaultTarget) ? 'text-emerald-700 dark:text-emerald-300' : 'text-gray-500 dark:text-gray-400'"
        >
          {{ progressFor(z.key, z.defaultTarget) }} / {{ z.defaultTarget }}
        </div>
        <div class="mt-2 h-1.5 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
          <div
            class="h-full rounded-full transition-all duration-200 ease-out"
            :class="isCardComplete(z.key, z.defaultTarget) ? 'bg-emerald-500' : 'bg-gray-500 dark:bg-gray-400'"
            :style="{ width: progressPercent(z.key, z.defaultTarget) + '%' }"
          />
        </div>
      </button>
    </div>

    <!-- Other Presets -->
    <div class="space-y-3">
      <h2 class="text-sm font-medium text-gray-700 dark:text-gray-300">More Counters</h2>
      <div class="grid grid-cols-2 gap-4">
        <button
          v-for="(z, index) in otherPresets"
          :key="z.key"
          @click="openZikr(z.key)"
          :class="[
            'rounded-xl py-4 px-3 bg-gray-50 border border-gray-100 hover:bg-gray-100 active:scale-[0.98] text-[15px] font-medium tracking-tight text-gray-900 dark:text-gray-100 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700 text-left',
            index === otherPresets.length - 1 && otherPresets.length % 2 !== 0 ? 'col-span-2' : '',
          ]"
        >
          <div>{{ z.label }} ({{ z.defaultTarget }})</div>
          <div class="text-xs mt-1 text-gray-500 dark:text-gray-400">
            {{ progressFor(z.key, z.defaultTarget) }} / {{ z.defaultTarget }}
          </div>
        </button>
      </div>
    </div>

    <InternalLinks current="zikr" />
  </div>
</template>

<style scoped>
.fade-in-enter-active,
.fade-in-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.fade-in-enter-from,
.fade-in-leave-to {
  opacity: 0;
  transform: translateY(-2px);
}

.fade-in-enter-to,
.fade-in-leave-from {
  opacity: 1;
  transform: translateY(0);
}
</style>
